/**
 * Shared condition formatting utilities for optimizer components.
 * This file exists to break circular dependencies between OptimizationActions.tsx and OptimizationHelper.tsx
 */

import React from 'react';
import { type ConditionProperties } from 'json-rules-engine';
import {
  getGroupConditions,
  getGroupOperator,
  type RuleTopLevelCondition,
} from '@bbkAdminComponents/experiences/audiences/edit-rule/rules-utils';

// Chip component for displaying conditions and actions
export const Chip: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <span className="tw-rounded-full tw-bg-neutral-100 tw-px-3 tw-py-1 tw-text-sm tw-text-neutral-900">
    {children}
  </span>
);

// Format operator for display
const formatOperator = (operator: string): string => {
  const operatorMap: Record<string, string> = {
    '==': 'is',
    '!=': 'is not',
    '>': 'is greater than',
    '>=': 'is greater than or equal to',
    '<': 'is less than',
    '<=': 'is less than or equal to',
    'String.in': 'is one of',
    'String.notIn': 'is not one of',
    'String.includes': 'contains',
    'String.startsWith': 'starts with',
    'String.notIncludes': 'does not contain',
    'Generic.isNilOrEmpty': 'is empty',
    'Generic.isNotNilOrEmpty': 'is not empty',
  };
  return operatorMap[operator] || operator;
};

// Format property name (remove prefix)
const formatPropertyName = (fact: string): string =>
  fact.replace(/^(custom|standard|reserved|alert|ai)\./, '');

// Format condition value for display
const formatConditionValue = (value: unknown): string => {
  if (value === null || value === undefined) {
    return '';
  }
  if (Array.isArray(value)) {
    return value.join(', ');
  }
  return String(value);
};

// Format a single condition as text
const formatCondition = (condition: ConditionProperties): string => {
  const property = formatPropertyName(condition.fact);
  const operator = formatOperator(condition.operator);
  const value = formatConditionValue(condition.value);
  return `${property} ${operator} ${value}`;
};

// Format all conditions from a rule
export const formatConditions = (
  condition: RuleTopLevelCondition
): string[] => {
  const conditions: string[] = [];
  const groupConditions = getGroupConditions(condition);

  groupConditions.forEach((cond) => {
    conditions.push(formatCondition(cond));
  });

  return conditions;
};

export const FormatConditionValues: React.FC<{
  condition: RuleTopLevelCondition;
}> = ({ condition }) => {
  const groupConditions = getGroupConditions(condition);
  const groupOperator = getGroupOperator(condition);
  const groupLabel = groupOperator === 'all' ? 'And' : 'Or';

  return (
    <>
      {groupConditions.map((cond, idx) => {
        const property = formatPropertyName(cond.fact);
        const operator = formatOperator(cond.operator);
        const value = formatConditionValue(cond.value);

        return (
          <div
            key={idx}
            className="tw-flex tw-flex-wrap tw-items-center tw-gap-mi tw-space-y-4"
          >
            <div className="tw-basis-12 tw-text-sm tw-text-neutral-600">
              {idx === 0 ? <>When</> : groupLabel}
            </div>
            <Chip>{property}</Chip>
            <Chip>{operator}</Chip>
            <Chip>{value}</Chip>
          </div>
        );
      })}
    </>
  );
};
